import { getRequiredMessage } from '@monetize/types/common';
import { z } from 'zod';

export enum TaxProviderTypeEnum {
  ANROK = 'ANROK',
  AVALARA = 'AVALARA',
  TAXWIRE = 'TAXWIRE',
  VERTEX = 'VERTEX',
}
export const TaxProviderTypeEnumZ = z.enum(TaxProviderTypeEnum);

export enum TaxEnvironments {
  SANDBOX = 'SANDBOX',
  PRODUCTION = 'PRODUCTION',
}
export const TaxEnvironmentsZ = z.enum(TaxEnvironments);

export enum LegalEntityApplicationScope {
  ALL = 'ALL',
  SCOPED = 'SCOPED',
}

export const LegalEntityApplicationScopeZ = z.enum(LegalEntityApplicationScope);

export enum TaxIdentifierEnum {
  PRODUCT_ID = 'PRODUCT_ID',
  SKU = 'SKU',
  CUSTOM_ID = 'CUSTOM_ID',
  TAX_ITEM_CODE = 'TAX_ITEM_CODE',
}

export const TaxIdentifierEnumZ = z.enum(TaxIdentifierEnum);

export enum TaxStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}
export const TaxStatusEnumZ = z.enum(TaxStatusEnum);

export const TaxConnectionBaseSchema = z.object({
  provider: TaxProviderTypeEnumZ,
  status: TaxStatusEnumZ,
});

export type ITaxConnectionBaseSchema = z.infer<typeof TaxConnectionBaseSchema>;

// Shared credentials schema used by both TaxConnectionSchema and TaxConnectionFormSchema
const TaxConnectionCredentialsSchema = z
  .object({
    /** Anrok */
    apiKey: z.string().nullish(), // Required if Provider is Anrok or Taxwire
    /** Avalara */
    accountNumber: z.string().nullish(), // Required if Provider is Avalara
    licenseKey: z.string().nullish(), // Required if Provider is Avalara
    environment: TaxEnvironmentsZ.nullish(), // Required if Provider is Avalara
    companyCode: z.string().nullish(), // Required if Provider is Avalara
    /** Vertex */
    type: z.string().nullish(), // Required if Provider is Vertex: OSeriesCloudCredentials or OnDemandCredentials
    identityProvider: z.string().nullish(), // Required if Provider is Vertex and type is OSeriesCloudCredentials: VERX or LEGACY
    baseUrl: z.string().nullish(), // Required if Provider is Vertex and type is OnDemandCredentials
    clientId: z.string().nullish(), // Required if Provider is Vertex
    clientSecret: z.string().nullish(), // Required if Provider is Vertex
  })
  .nullable();

type TaxConnectionRefineInput = {
  provider: TaxProviderTypeEnum;
  credentials: {
    apiKey?: string | null;
    accountNumber?: string | null;
    licenseKey?: string | null;
    environment?: TaxEnvironments | null;
    companyCode?: string | null;
    type?: string | null;
    identityProvider?: string | null;
    baseUrl?: string | null;
    clientId?: string | null;
    clientSecret?: string | null;
  } | null;
  configurations: {
    identifier?: TaxIdentifierEnum | null;
    legalEntityOverride: LegalEntityApplicationScope;
    legalEntities: string[];
    validateAddress?: boolean | null;
    postAndCommitTransactions?: boolean | null;
    enableLogging?: boolean | null;
  };
};

const validateTaxConnection = (
  val: TaxConnectionRefineInput,
  ctx: z.RefinementCtx,
) => {
  if (
    val.provider === TaxProviderTypeEnum.ANROK ||
    val.provider === TaxProviderTypeEnum.TAXWIRE
  ) {
    if (val.credentials && !val.credentials.apiKey) {
      ctx.addIssue({
        code: 'custom',
        message: getRequiredMessage('API Key'),
        path: ['credentials.apiKey'],
      });
    }

    if (!val.configurations.identifier) {
      ctx.addIssue({
        code: 'custom',
        message: getRequiredMessage('Identifier'),
        path: ['configurations.identifier'],
      });
    }
  }

  if (val.provider === TaxProviderTypeEnum.AVALARA) {
    if (val.credentials) {
      if (!val.credentials.accountNumber) {
        ctx.addIssue({
          code: 'custom',
          message: getRequiredMessage('Account Number'),
          path: ['credentials.accountNumber'],
        });
      }
      if (!val.credentials.licenseKey) {
        ctx.addIssue({
          code: 'custom',
          message: getRequiredMessage('License Key'),
          path: ['credentials.licenseKey'],
        });
      }
      if (!val.credentials.environment) {
        ctx.addIssue({
          code: 'custom',
          message: getRequiredMessage('Environment'),
          path: ['credentials.environment'],
        });
      }
      if (!val.credentials.companyCode) {
        ctx.addIssue({
          code: 'custom',
          message: getRequiredMessage('Company Code'),
          path: ['credentials.companyCode'],
        });
      }
    }
  }

  if (val.provider === TaxProviderTypeEnum.VERTEX) {
    if (val.credentials) {
      if (!val.credentials.type) {
        ctx.addIssue({
          code: 'custom',
          message: getRequiredMessage('Credential Type'),
          path: ['credentials.type'],
        });
      }
      if (!val.credentials.clientId) {
        ctx.addIssue({
          code: 'custom',
          message: getRequiredMessage('Client ID'),
          path: ['credentials.clientId'],
        });
      }
      if (!val.credentials.clientSecret) {
        ctx.addIssue({
          code: 'custom',
          message: getRequiredMessage('Client Secret'),
          path: ['credentials.clientSecret'],
        });
      }
      if (!val.credentials.companyCode) {
        ctx.addIssue({
          code: 'custom',
          message: getRequiredMessage('Company Code'),
          path: ['credentials.companyCode'],
        });
      }

      // Conditional validation based on credential type
      if (val.credentials.type === 'OSeriesCloudCredentials') {
        if (!val.credentials.identityProvider) {
          ctx.addIssue({
            code: 'custom',
            message: getRequiredMessage('Identity Provider'),
            path: ['credentials.identityProvider'],
          });
        }
      }

      if (val.credentials.type === 'OnDemandCredentials') {
        if (!val.credentials.baseUrl) {
          ctx.addIssue({
            code: 'custom',
            message: getRequiredMessage('Base URL'),
            path: ['credentials.baseUrl'],
          });
        }
      }
    }

    if (!val.configurations.identifier) {
      ctx.addIssue({
        code: 'custom',
        message: getRequiredMessage('Identifier'),
        path: ['configurations.identifier'],
      });
    }
  }

  if (
    val.configurations.legalEntityOverride ===
      LegalEntityApplicationScope.SCOPED &&
    val.configurations.legalEntities.length === 0
  ) {
    ctx.addIssue({
      code: 'custom',
      message: 'At least one legal entity is required.',
      path: ['configurations.legalEntities'],
    });
  }
};

export const TaxConnectionSchema = TaxConnectionBaseSchema.extend({
  provider: TaxProviderTypeEnumZ,
  credentials: TaxConnectionCredentialsSchema,
  configurations: z.object({
    /** Anrok and Avalara and Taxwire */
    identifier: TaxIdentifierEnumZ.nullish(), // Required for Anrok, Avalara and Taxwire
    legalEntityOverride: LegalEntityApplicationScopeZ.default(
      LegalEntityApplicationScope.ALL,
    ),
    legalEntities: z.string().array().default([]),
    /** Avalara */
    validateAddress: z.boolean().nullish(),
    postAndCommitTransactions: z.boolean().nullish(),
    enableLogging: z.boolean().nullish(),
  }),
  status: TaxStatusEnumZ,
})
  .superRefine((val, ctx) => validateTaxConnection(val, ctx))
  .transform((val) => {
    if (val.provider === TaxProviderTypeEnum.ANROK) {
      return {
        ...val,
        credentials: val.credentials
          ? {
              apiKey: val.credentials.apiKey,
            }
          : null,
        configurations: {
          identifier: val.configurations.identifier,
          legalEntityOverride: val.configurations.legalEntityOverride,
          legalEntities:
            val.configurations.legalEntityOverride ===
            LegalEntityApplicationScope.SCOPED
              ? val.configurations.legalEntities
              : [],
        },
      };
    }
    if (val.provider === TaxProviderTypeEnum.AVALARA) {
      return {
        ...val,
        credentials: val.credentials
          ? {
              accountNumber: val.credentials.accountNumber,
              licenseKey: val.credentials.licenseKey,
              environment: val.credentials.environment,
              companyCode: val.credentials.companyCode,
            }
          : null,
        configurations: {
          identifier: val.configurations.identifier,
          legalEntityOverride: val.configurations.legalEntityOverride,
          legalEntities:
            val.configurations.legalEntityOverride ===
            LegalEntityApplicationScope.SCOPED
              ? val.configurations.legalEntities
              : [],
          validateAddress: val.configurations.validateAddress ?? false,
          postAndCommitTransactions:
            val.configurations.postAndCommitTransactions ?? false,
          enableLogging: val.configurations.enableLogging ?? false,
        },
      };
    }

    if (val.provider === TaxProviderTypeEnum.TAXWIRE) {
      return {
        ...val,
        credentials: val.credentials
          ? {
              apiKey: val.credentials.apiKey,
            }
          : null,
        configurations: {
          identifier: val.configurations.identifier,
          legalEntityOverride: val.configurations.legalEntityOverride,
          legalEntities:
            val.configurations.legalEntityOverride ===
            LegalEntityApplicationScope.SCOPED
              ? val.configurations.legalEntities
              : [],
        },
      };
    }

    if (val.provider === TaxProviderTypeEnum.VERTEX) {
      const baseCredentials = val.credentials
        ? {
            type: val.credentials.type,
            clientId: val.credentials.clientId,
            clientSecret: val.credentials.clientSecret,
            companyCode: val.credentials.companyCode,
          }
        : null;

      // Add conditional fields based on credential type
      const credentials = baseCredentials
        ? {
            ...baseCredentials,
            ...(val.credentials?.type === 'OSeriesCloudCredentials' && {
              identityProvider: val.credentials.identityProvider,
            }),
            ...(val.credentials?.type === 'OnDemandCredentials' && {
              baseUrl: val.credentials.baseUrl,
            }),
          }
        : null;

      return {
        ...val,
        credentials,
        configurations: {
          identifier: val.configurations.identifier,
          legalEntityOverride: val.configurations.legalEntityOverride,
          legalEntities:
            val.configurations.legalEntityOverride ===
            LegalEntityApplicationScope.SCOPED
              ? val.configurations.legalEntities
              : [],
        },
      };
    }

    return val;
  });

// Form schema without transform for react-hook-form compatibility
export const TaxConnectionFormSchema = TaxConnectionBaseSchema.extend({
  provider: TaxProviderTypeEnumZ,
  credentials: TaxConnectionCredentialsSchema,
  configurations: z.object({
    /** Anrok and Avalara and Taxwire */
    identifier: TaxIdentifierEnumZ.nullish(), // Required for Anrok, Avalara and Taxwire
    legalEntityOverride: LegalEntityApplicationScopeZ, // Required, provided via defaultValues
    legalEntities: z.string().array(), // Required, provided via defaultValues
    /** Avalara */
    validateAddress: z.boolean().nullish(),
    postAndCommitTransactions: z.boolean().nullish(),
    enableLogging: z.boolean().nullish(),
  }),
  status: TaxStatusEnumZ,
}).superRefine((val, ctx) => validateTaxConnection(val, ctx));

export type ITaxConnectionFormSchema = z.infer<typeof TaxConnectionFormSchema>;

export const TaxConnectionVerifyReqSchema = z.object({
  provider: TaxProviderTypeEnumZ,
  credentials: z
    .union([
      z.object({
        apiKey: z.string(),
      }),
      z.object({
        accountNumber: z.string(),
        licenseKey: z.string(),
        environment: z.string(),
        companyCode: z.string(),
      }),
      z.object({
        type: z.string(),
        clientId: z.string(),
        clientSecret: z.string(),
        identityProvider: z.string().optional(),
        baseUrl: z.string().optional(),
      }),
    ])
    .nullable(),
});

export type ITaxConnectionVerifyReqSchema = z.infer<
  typeof TaxConnectionVerifyReqSchema
>;

// Discriminated union types for each provider
export type IAnrokTaxConnectionSchema = {
  provider: TaxProviderTypeEnum.ANROK;
  status: TaxStatusEnum;
  credentials: {
    apiKey: string | null;
  } | null;
  configurations: {
    identifier: TaxIdentifierEnum | null;
    legalEntityOverride: LegalEntityApplicationScope;
    legalEntities: string[];
  };
};

export type IAvalaraTaxConnectionSchema = {
  provider: TaxProviderTypeEnum.AVALARA;
  status: TaxStatusEnum;
  credentials: {
    accountNumber: string | null;
    licenseKey: string | null;
    environment: TaxEnvironments | null;
    companyCode: string | null;
  } | null;
  configurations: {
    identifier: TaxIdentifierEnum | null;
    legalEntityOverride: LegalEntityApplicationScope;
    legalEntities: string[];
    validateAddress: boolean;
    postAndCommitTransactions: boolean;
    enableLogging: boolean;
  };
};

export type ITaxwireTaxConnectionSchema = {
  provider: TaxProviderTypeEnum.TAXWIRE;
  status: TaxStatusEnum;
  credentials: {
    apiKey: string | null;
  } | null;
  configurations: {
    identifier: TaxIdentifierEnum | null;
    legalEntityOverride: LegalEntityApplicationScope;
    legalEntities: string[];
  };
};

export type IVertexTaxConnectionSchema = {
  provider: TaxProviderTypeEnum.VERTEX;
  status: TaxStatusEnum;
  credentials: {
    type: string | null;
    clientId: string | null;
    clientSecret: string | null;
    companyCode: string | null;
    identityProvider?: string | null;
    baseUrl?: string | null;
  } | null;
  configurations: {
    identifier: TaxIdentifierEnum | null;
    legalEntityOverride: LegalEntityApplicationScope;
    legalEntities: string[];
  };
};

// Re-export ITaxConnectionSchema as a proper discriminated union
export type ITaxConnectionSchema =
  | IAnrokTaxConnectionSchema
  | IAvalaraTaxConnectionSchema
  | ITaxwireTaxConnectionSchema
  | IVertexTaxConnectionSchema;
